// ─────────────────────────────────────────────
//  NavMenu — single dropdown that replaces the fixed left sidebar.
//
//  Repurposes the old "All locations" select: one button opens a menu with
//  app navigation (OR Board, My Room, Daily List, Setup ▸ submenu) AND board
//  location selection. Self-contained (own styles, own auth/location reads) so
//  every page can drop it into its topbar. Responsive by nature — a dropdown
//  works the same on phone, tablet, and desktop.
//
//  Location picks navigate to /board?loc=<key>; BoardPage reads that query param
//  so location selection works from any page.
// ─────────────────────────────────────────────
import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useLocations } from "../hooks/useLocations";
import { useFacilities } from "../hooks/useFacilities";
import { resolveFacilityStatus } from "../domain/facilityRules";
import { facilityLabel, shortFacilityLabel, commonOrgPrefix } from "../domain/facilities";
import { canSeeTeamDirectory } from "../domain/teamDirectory";
import { cacheOrgName, resolveOrgName } from "../config/brand";
import { updateUser } from "../firebase/firestore";
import { useToast } from "../context/ToastContext";
import tokens from "../theme/tokens";

const { color, radius, shadow, font } = tokens;

// Setup sub-pages, in the order the user specified. Each routes to
// /setup?tab=<id>, which SetupPage reads to show that section.
const SETUP_ITEMS = [
  { id: "facility",      label: "Facility" },
  { id: "rooms",         label: "Rooms" },
  { id: "users",         label: "Users" },
  { id: "aic",           label: "AIC Assignment" },
  { id: "map",           label: "Provider Map", adminOnly: true },
  { id: "testing",       label: "Testing Mode", adminOnly: true },
  { id: "package",       label: "Package & Billing" },
  { id: "notifications", label: "Notifications" },
];

export default function NavMenu({ compact = false }) {
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  const auth = useAuth();
  const { user, isAIC, isAdmin, realIsAdmin, canManageDailyList, isObserver, isScheduler, isBoardManager } = auth;
  const { active: activeLocs } = useLocations(user?.activeFacilityId);
  const toast = useToast();

  // ── Facility switcher (Phase 5.4, SC2) ──
  // Everything downstream (board, assignments, messaging, alerts) already
  // filters by user.activeFacilityId, so switching = updating that field on
  // the user's own doc (rules-legal: role/isAIC/facilityIds stay untouched).
  // Admins see every non-archived facility; everyone else sees the ACTIVE
  // facilities they belong to.
  const allFacilities = useFacilities();
  const myFacilityIds = Array.isArray(user?.facilityIds) ? user.facilityIds : [];
  const facilityOptions = allFacilities.filter((f) => {
    const status = resolveFacilityStatus(f);
    if (isAdmin) return status !== "archived";
    return status === "active" && myFacilityIds.includes(f.id);
  });
  const showFacilities = facilityOptions.length > 1;
  const switchFacility = async (f) => {
    if (f.id === user?.activeFacilityId) { close(); return; }
    try {
      await updateUser(user.uid, { activeFacilityId: f.id, activeFacilityName: f.label || f.name || f.id });
      toast.success(`Now viewing ${f.label || f.name}.`);
      close();
      navigate("/board?loc=all");
    } catch (err) {
      console.error("Switch facility failed:", err);
      toast.error("Could not switch facility.");
    }
  };

  const [open, setOpen] = useState(false);
  const [setupOpen, setSetupOpen] = useState(false);
  const rootRef = useRef(null);

  const params = new URLSearchParams(search);
  const currentLoc = params.get("loc") || "all";
  const currentTab = params.get("tab") || "facility";
  const showLocations = (isAIC || isAdmin) && activeLocs.length > 0;

  // Close on outside click + Escape. Uses only state setters (stable) so no
  // extra effect dependencies are needed.
  useEffect(() => {
    if (!open) return undefined;
    const shut = () => { setOpen(false); setSetupOpen(false); };
    const onDown = (e) => { if (rootRef.current && !rootRef.current.contains(e.target)) shut(); };
    const onKey = (e) => { if (e.key === "Escape") shut(); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const close = () => { setOpen(false); setSetupOpen(false); };
  const go = (path) => { navigate(path); close(); };

  // What page are we on (drives the button label + active highlight).
  const onSetup = pathname.startsWith("/setup");
  // A Scheduler's only surface is the scheduling module; a Board Manager's
  // surfaces are the OR board + timeline chart (shared wall device — no
  // personal pages, no daily list, no messaging).
  const navItems = [
    // The post-login landing. Hidden from the two roles the /home route bounces
    // anyway (a Scheduler to /schedule, a wall device to /board), so the menu
    // never offers a door that closes in your face.
    { label: "Home",       path: "/home",       icon: "🏠", show: !isScheduler && !isBoardManager, active: pathname === "/home" },
    { label: "OR Board",   path: "/board",      icon: "📋", show: !isScheduler,          active: pathname === "/board" },
    // Observers monitor the full board only — no personal room view.
    { label: "My Room",    path: "/room",       icon: "🏥", show: !isObserver && !isScheduler && !isBoardManager, active: pathname === "/room" },
    // Daily Assignments — Administrators + Schedulers only (scheduling surface).
    // Room Status — the four case phases. Owner, 2026-09-07: show it to "crna,
    // aa, residents, supervisors and admin".
    //
    // ⚠️ THE LIST IS EXPRESSED AS WHO IS EXCLUDED, not as a set of roles to
    // match. Naming the five would mean a sixth clinical role added later
    // silently loses a page nobody remembers to re-grant — and every role this
    // app has EXCEPT the three below is one of the five. An Observer is
    // read-only by definition and stamps nothing; a Scheduler never touches the
    // live board; a wall device is not a person.
    //
    // The page itself refuses gracefully ("No room is assigned to you right
    // now"), and domain/caseRole decides per room whether a viewer gets the
    // stamp buttons, Acknowledge, or neither — so this entry never opens onto a
    // permission error.
    { label: "Room Status", path: "/room-status", icon: "🩺", show: !isObserver && !isScheduler && !isBoardManager, active: pathname === "/room-status" },
    { label: "Daily List", path: "/daily-list", icon: "🗓️", show: canManageDailyList, active: pathname === "/daily-list" },
    // Timeline is view-only for CRNAs/observers (case editing stays gated by
    // canManageAssignments inside the page); show it to everyone alongside
    // OR Board + My Room so CRNAs get it as a third nav option.
    //
    // ⚠️ THIS DELIBERATELY DIFFERS FROM THE DASHBOARD, which sends a CRNA/AA/
    // resident to /timeline-light (HomePage.timelineRouteFor). Sending them
    // there from here as well would REMOVE a working read-only chart and give
    // them a page with nothing on it — a regression dressed as consistency.
    // Point this entry at timelineRouteFor once Timeline Light renders a day.
    // ⚠️ OR Board A / B / C ARE OFF THIS MENU ON PURPOSE (2026-08-17, asked for).
    // They were three alternative layouts published side by side so they could be
    // compared on a real day rather than in a mockup. That comparison is over,
    // and three near-identical "OR Board" entries above the one people actually
    // use is a way to open the wrong board mid-case.
    //
    // THE ROUTES STAY MOUNTED (see App.js) and the title map below still names
    // them. Removing an entry from a menu is reversible; deleting the pages would
    // break every bookmark and saved link that already points at them, and the
    // board layout switcher on the board page still offers these layouts.
    { label: "Timeline Chart", path: "/timeline", icon: "📊", show: !isScheduler, active: pathname === "/timeline" },
    // The department contact directory — the personal mobile number of everyone
    // in the department, so: the three roles the Firestore rules allow, at one
    // of this tenant's three campuses, and nobody else. The predicate is the
    // SAME function the landing door and the page use; a menu entry that opens
    // onto a permission error reads as a broken app.
    { label: "Team",       path: "/team",       icon: "📇", show: canSeeTeamDirectory(auth), active: pathname === "/team" },
    // Internal scheduling module — Admins + Schedulers only.
    { label: "Schedule", path: "/schedule", icon: "📅", show: isAdmin || isScheduler, active: pathname === "/schedule" },
  ];
  const buttonLabel =
    onSetup ? "Setup"
    : pathname === "/home" ? "Home"
    : pathname === "/messages" ? "Messages"
    // ⚠️ WITHOUT THIS BRANCH THE BURGER READS "OR Board" WHILE YOU ARE ON TEAM.
    // The chain's fallback is the board, so every new route needs an entry here
    // as well as one in navItems above.
    : pathname === "/team" ? "Team"
    : pathname === "/timeline-light" ? "Timeline"
    : pathname === "/room" ? "My Room"
    : pathname === "/room-status" ? "Room Status"
    : pathname === "/daily-list" ? "Daily List"
    : pathname === "/board-a" ? "OR Board A"
    : pathname === "/board-b" ? "OR Board B"
    : (pathname === "/board-c" || pathname === "/panel") ? "OR Board C"
    : pathname === "/timeline" ? "Timeline Chart"
    : pathname === "/schedule" ? "Schedule"
    : "OR Board";

  const itemStyle = (active) => ({
    ...st.item,
    ...(active ? st.itemActive : {}),
  });

  // Current facility for the topbar tag. Prefer the LIVE facility doc's name
  // for the active id (a tenant renames its own campuses; the built-in list is
  // only a seed) — user.activeFacilityName can hold stale legacy values (seen
  // live: "Main OR") — and fall back to the stored display name only for
  // facility ids nothing knows yet.
  const facilityNames = allFacilities.map((f) => f.name || f.label).filter(Boolean);
  const labelFromId = user?.activeFacilityId
    ? facilityLabel(user.activeFacilityId, allFacilities)
    : "";
  const idIsKnown = labelFromId && labelFromId !== user?.activeFacilityId;
  const facilityFullName = idIsKnown ? labelFromId : (user?.activeFacilityName || labelFromId);
  // Shorten using the operator prefix THIS tenant's campuses share, not a
  // hardcoded "AdventHealth" (Track C).
  const facilityName = shortFacilityLabel(facilityFullName, facilityNames);

  // Teach this device the tenant's operator name while we have the live docs,
  // so the SIGNED-OUT login screen can show it (rules block facility reads
  // there). Falls back to the current facility's own full name for a
  // single-campus tenant, where there is no shared prefix to find.
  // (A plain key, not an expression in the deps array — CRA lints that, and an
  // eslint-disable for react-hooks/exhaustive-deps is a BUILD ERROR here
  // because the rule isn't configured in this project.)
  const facilityNamesKey = facilityNames.join("|");
  useEffect(() => {
    const org = resolveOrgName(facilityNamesKey ? facilityNamesKey.split("|") : [], commonOrgPrefix);
    cacheOrgName(org || facilityFullName);
  }, [facilityNamesKey, facilityFullName]);

  return (
    <div ref={rootRef} style={st.root}>
      <button
        type="button"
        style={{ ...st.trigger, ...(compact ? st.triggerCompact : {}) }}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Navigation menu"
        onClick={() => setOpen((o) => !o)}
      >
        <span aria-hidden="true" style={st.burger}>☰</span>
        {/* Compact (phone): burger only, so the topbar never crowds the alert
            buttons. The current page is obvious from the board itself. */}
        {!compact && <span style={st.triggerLabel}>{buttonLabel}</span>}
        {!compact && <span aria-hidden="true" style={st.caret}>▾</span>}
      </button>

      {/* Always-visible current facility, so nobody ever acts on the wrong
          campus. Full name in the tooltip; short name (operator prefix dropped)
          in the bar. Reads the same activeFacilityId every surface filters by. */}
      {facilityName && (
        <span style={{ ...st.facilityTag, ...(compact ? st.facilityTagCompact : {}) }} title={facilityFullName}>
          <span aria-hidden="true">📍</span> {facilityName}
        </span>
      )}

      {open && (
        <div role="menu" style={st.panel}>
          <div style={st.sectionLabel}>Navigate</div>
          {navItems.filter((i) => i.show).map((i) => (
            <button key={i.path} role="menuitem" style={itemStyle(i.active)} onClick={() => go(i.path)}>
              <span aria-hidden="true" style={st.icon}>{i.icon}</span>
              <span style={st.itemText}>{i.label}</span>
            </button>
          ))}

          {(isAIC || isAdmin) && (
            <>
              <button
                role="menuitem"
                aria-haspopup="menu"
                aria-expanded={setupOpen}
                style={itemStyle(onSetup)}
                onClick={() => setSetupOpen((o) => !o)}
              >
                <span aria-hidden="true" style={st.icon}>⚙️</span>
                <span style={st.itemText}>Setup</span>
                <span aria-hidden="true" style={st.subCaret}>{setupOpen ? "▾" : "▸"}</span>
              </button>
              {setupOpen && (
                <div style={st.submenu}>
                  {SETUP_ITEMS.filter((t) => !t.adminOnly || realIsAdmin).map((t) => (
                    <button
                      key={t.id}
                      role="menuitem"
                      style={itemStyle(onSetup && currentTab === t.id)}
                      onClick={() => go(`/setup?tab=${t.id}`)}
                    >
                      <span style={{ ...st.itemText, paddingLeft: 6 }}>{t.label}</span>
                    </button>
                  ))}
                </div>
              )}
            </>
          )}

          {showFacilities && (
            <>
              <div style={st.divider} />
              <div style={st.sectionLabel}>Facility</div>
              {facilityOptions.map((f) => (
                <button
                  key={f.id}
                  role="menuitem"
                  style={itemStyle(f.id === user?.activeFacilityId)}
                  onClick={() => switchFacility(f)}
                >
                  <span aria-hidden="true" style={st.icon}>{f.id === user?.activeFacilityId ? "✓" : "🏥"}</span>
                  <span style={st.itemText}>{f.label || f.name || f.id}</span>
                </button>
              ))}
            </>
          )}

          {showLocations && (
            <>
              <div style={st.divider} />
              <div style={st.sectionLabel}>Board location</div>
              <button role="menuitem" style={itemStyle(currentLoc === "all")} onClick={() => go("/board?loc=all")}>
                <span aria-hidden="true" style={st.icon}>{currentLoc === "all" ? "✓" : ""}</span>
                <span style={st.itemText}>All locations</span>
              </button>
              {activeLocs.map((l) => (
                <button key={l.key} role="menuitem" style={itemStyle(currentLoc === l.key)} onClick={() => go(`/board?loc=${l.key}`)}>
                  <span aria-hidden="true" style={st.icon}>{currentLoc === l.key ? "✓" : ""}</span>
                  <span style={st.itemText}>{l.label}</span>
                </button>
              ))}
            </>
          )}
        </div>
      )}
    </div>
  );
}

const st = {
  root:        { position: "relative", display: "inline-flex", alignItems: "center" },
  facilityTag: { marginLeft: 10, display: "inline-flex", alignItems: "center", gap: 4, fontSize: font.size.label, fontWeight: font.weight.bold, color: color.accent.primary, whiteSpace: "nowrap", letterSpacing: ".2px" },
  facilityTagCompact: { marginLeft: 8, fontSize: 11 },
  trigger:     { display: "flex", alignItems: "center", gap: 8, background: color.surface.raised, border: `1px solid ${color.border.default}`, borderRadius: radius.full, padding: "5px 12px", fontSize: font.size.body, fontFamily: "inherit", color: color.text.secondary, cursor: "pointer", whiteSpace: "nowrap", flexShrink: 0 },
  triggerCompact: { padding: "7px 11px", gap: 0 },
  burger:      { fontSize: 15, lineHeight: 1 },
  triggerLabel:{ fontWeight: font.weight.bold },
  caret:       { fontSize: 10, color: color.text.muted },
  panel:       { position: "absolute", top: "calc(100% + 6px)", left: 0, minWidth: 230, maxWidth: "90vw", maxHeight: "80vh", overflowY: "auto", background: color.surface.card, border: `1px solid ${color.border.default}`, borderRadius: radius.md, boxShadow: shadow.lg, padding: 6, zIndex: 100 },
  sectionLabel:{ padding: "8px 12px 4px", fontSize: font.size.label, fontWeight: font.weight.bold, color: color.text.muted, textTransform: "uppercase", letterSpacing: ".8px" },
  item:        { display: "flex", alignItems: "center", gap: 8, width: "100%", textAlign: "left", background: "transparent", border: "none", borderRadius: radius.sm, padding: "9px 12px", fontSize: font.size.body, fontFamily: "inherit", color: color.text.secondary, cursor: "pointer", minHeight: 40 },
  itemActive:  { background: "#1e40af12", color: color.accent.primary, fontWeight: font.weight.bold },
  icon:        { width: 18, display: "inline-flex", justifyContent: "center", flexShrink: 0 },
  itemText:    { flex: 1 },
  subCaret:    { fontSize: 11, color: color.text.muted },
  submenu:     { paddingLeft: 14, borderLeft: `2px solid ${color.border.default}`, marginLeft: 16 },
  divider:     { height: 1, background: color.border.default, margin: "6px 8px" },
};
